<script>
import SpellCheckedText from "@/components/SpellCheckedText.vue";
import PhotoBox from "@/components/PhotoBox.vue";

const FIRST_PAGE_SIZE = 13;
const OTHER_PAGE_SIZE = 16; // Standard grid for overflow pages (4x4)

export default {
    name: "CPTemp15",
    components: {
        SpellCheckedText,
        PhotoBox
    },
    props: {
        projects: { type: Object, required: true },
        pageGap: { type: Boolean, default: true },
        startPage: { type: Number, default: 1 },
    },
    data() {
        return {
            footerImage: ""
        };
    },
    computed: {
        projectPages() {
            const pages = [];
            if (this.projects && this.projects.projects) {
                const list = this.projects.projects;
                // First page
                pages.push(list.slice(0, FIRST_PAGE_SIZE));
                // Remaining pages
                for (let i = FIRST_PAGE_SIZE; i < list.length; i += OTHER_PAGE_SIZE) {
                    pages.push(list.slice(i, i + OTHER_PAGE_SIZE));
                }
            }
            if (pages.length === 0) pages.push([]);
            return pages;
        }
    },
    methods: {
        generateGradients() {
            const fCanvas = document.createElement('canvas');
            fCanvas.width = 800;
            fCanvas.height = 300;
            const fCtx = fCanvas.getContext('2d');
            const fGrad = fCtx.createLinearGradient(0, 300, 0, 0);
            fGrad.addColorStop(0, 'rgba(0, 0, 0, 0.8)');
            fGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
            fCtx.fillStyle = fGrad;
            fCtx.fillRect(0, 0, 800, 300);
            this.footerImage = fCanvas.toDataURL();
        }
    },
    mounted() {
        this.generateGradients();
    }
};
</script>

<template>
    <div class="main-container" :style="{
        gap: pageGap ? '20px' : '0px',
        '--header-color': projects.style.colorScheme.header,
        '--body-color': projects.style.colorScheme.body,
        '--footer-color': projects.style.colorScheme.footer,
        '--heading-color': projects.style.heading.color,
        '--heading-size': projects.style.heading.size + 'px',
        '--heading-font': projects.style.heading.font,
        '--heading-weight': projects.style.heading.bold ? 'bold' : 'normal',
        '--heading-style': projects.style.heading.italic ? 'italic' : 'normal',
        '--heading-decoration': projects.style.heading.underline ? 'underline' : 'none',
        '--label-color': projects.style.label.color,
        '--label-size': projects.style.label.size + 'px',
        '--label-font': projects.style.label.font,
        '--label-weight': projects.style.label.bold ? 'bold' : 'normal',
        '--label-style': projects.style.label.italic ? 'italic' : 'normal',
        '--label-decoration': projects.style.label.underline ? 'underline' : 'none',
        '--text-color': projects.style.text.color,
        '--text-size': projects.style.text.size + 'px',
        '--text-font': projects.style.text.font,
        '--text-weight': projects.style.text.bold ? 'bold' : 'normal',
        '--text-style': projects.style.text.italic ? 'italic' : 'normal',
        '--text-decoration': projects.style.text.underline ? 'underline' : 'none',
    }">
        <div v-for="(page, pageIndex) in projectPages" :key="pageIndex" class="sub-container page-unit">
            <div class="page-inner">
                <!-- PROJECT CONTENT ROWS -->
                <div class="project-rows" :class="{ 'first-page': pageIndex === 0 }">
                    <template v-if="pageIndex === 0">
                        <!-- ROW 1: About Box + Project 0 -->
                        <div class="content-row row-1">
                            <div class="about-box">
                                <div class="main-header">
                                    <SpellCheckedText :value="projects.title || ''" @input="projects.title = $event" />
                                </div>
                                <div class="about-sec">
                                    <h3 class="about-title">
                                        <SpellCheckedText :value="(projects.about && projects.about.title) || ''"
                                            @input="projects.about.title = $event" />
                                    </h3>
                                    <div class="about-description">
                                        <SpellCheckedText :value="(projects.about && projects.about.description) || ''"
                                            @input="projects.about.description = $event" />
                                    </div>
                                </div>
                            </div>
                            <div class="project-card card-normal">
                                <div v-if="page[0]" class="card-inner">
                                    <PhotoBox class="photobox" :project="page[0]"
                                        :bgColor="projects.style.colorScheme.body" />
                                    <div class="overlay">
                                        <p class="p-name">
                                            <SpellCheckedText :value="page[0].projectName || 'N/A'"
                                                @input="page[0].projectName = $event" />
                                        </p>
                                        <p class="p-loc">
                                            <SpellCheckedText :value="page[0].city || 'N/A'"
                                                @input="page[0].city = $event" />,
                                            <SpellCheckedText :value="page[0].stateProvince || 'N/A'"
                                                @input="page[0].stateProvince = $event" />
                                        </p>
                                    </div>

                                    <div v-if="footerImage" class="bottom-gradient">
                                        <img :src="footerImage" class="w-full h-full" />
                                    </div>
                                </div>
                                <div v-else class="empty-slot" />
                            </div>
                        </div>

                        <!-- ROW 2: Projects 1-4 -->
                        <div class="content-row row-2">
                            <div class="project-card card-xlarge">
                                <div v-if="page[1]" class="card-inner">
                                    <PhotoBox class="photobox" :project="page[1]"
                                        :bgColor="projects.style.colorScheme.body" />
                                    <div class="overlay">
                                        <p class="p-name">
                                            <SpellCheckedText :value="page[1].projectName || 'N/A'"
                                                @input="page[1].projectName = $event" />
                                        </p>
                                        <p class="p-loc">
                                            <SpellCheckedText :value="page[1].city || 'N/A'"
                                                @input="page[1].city = $event" />,
                                            <SpellCheckedText :value="page[1].stateProvince || 'N/A'"
                                                @input="page[1].stateProvince = $event" />
                                        </p>
                                    </div>
                                    <div v-if="footerImage" class="bottom-gradient">
                                        <img :src="footerImage" class="w-full h-full" />
                                    </div>
                                </div>
                                <div v-else class="empty-slot" />
                            </div>
                            <div class="project-card card-normal">
                                <div v-if="page[2]" class="card-inner">
                                    <PhotoBox class="photobox" :project="page[2]"
                                        :bgColor="projects.style.colorScheme.body" />
                                    <div class="overlay">
                                        <p class="p-name">
                                            <SpellCheckedText :value="page[2].projectName || 'N/A'"
                                                @input="page[2].projectName = $event" />
                                        </p>
                                        <p class="p-loc">
                                            <SpellCheckedText :value="page[2].city || 'N/A'"
                                                @input="page[2].city = $event" />,
                                            <SpellCheckedText :value="page[2].stateProvince || 'N/A'"
                                                @input="page[2].stateProvince = $event" />
                                        </p>
                                    </div>
                                    <div v-if="footerImage" class="bottom-gradient">
                                        <img :src="footerImage" class="w-full h-full" />
                                    </div>
                                </div>
                                <div v-else class="empty-slot" />
                            </div>
                            <div class="project-card card-small">
                                <div v-if="page[3]" class="card-inner">
                                    <PhotoBox class="photobox" :project="page[3]"
                                        :bgColor="projects.style.colorScheme.body" />
                                    <div class="overlay">
                                        <p class="p-name">
                                            <SpellCheckedText :value="page[3].projectName || 'N/A'"
                                                @input="page[3].projectName = $event" />
                                        </p>
                                        <p class="p-loc">
                                            <SpellCheckedText :value="page[3].city || 'N/A'"
                                                @input="page[3].city = $event" />,
                                            <SpellCheckedText :value="page[3].stateProvince || 'N/A'"
                                                @input="page[3].stateProvince = $event" />
                                        </p>
                                    </div>
                                    <div v-if="footerImage" class="bottom-gradient">
                                        <img :src="footerImage" class="w-full h-full" />
                                    </div>
                                </div>
                                <div v-else class="empty-slot" />
                            </div>
                            <div class="project-card card-normal">
                                <div v-if="page[4]" class="card-inner">
                                    <PhotoBox class="photobox" :project="page[4]"
                                        :bgColor="projects.style.colorScheme.body" />
                                    <div class="overlay">
                                        <p class="p-name">
                                            <SpellCheckedText :value="page[4].projectName || 'N/A'"
                                                @input="page[4].projectName = $event" />
                                        </p>
                                        <p class="p-loc">
                                            <SpellCheckedText :value="page[4].city || 'N/A'"
                                                @input="page[4].city = $event" />,
                                            <SpellCheckedText :value="page[4].stateProvince || 'N/A'"
                                                @input="page[4].stateProvince = $event" />
                                        </p>
                                    </div>
                                    <div v-if="footerImage" class="bottom-gradient">
                                        <img :src="footerImage" class="w-full h-full" />
                                    </div>
                                </div>
                                <div v-else class="empty-slot" />
                            </div>
                        </div>

                        <!-- ROWS 3 & 4: Projects 5-11 -->
                        <div class="bottom-wrapper">
                            <div class="left-col">
                                <!-- ROW 3 -->
                                <div class="content-row row-3">
                                    <div class="project-card card-normal">
                                        <div v-if="page[5]" class="card-inner">
                                            <PhotoBox class="photobox" :project="page[5]"
                                                :bgColor="projects.style.colorScheme.body" />
                                            <div class="overlay">
                                                <p class="p-name">
                                                    <SpellCheckedText :value="page[5].projectName || 'N/A'"
                                                        @input="page[5].projectName = $event" />
                                                </p>
                                                <p class="p-loc">
                                                    <SpellCheckedText :value="page[5].city || 'N/A'"
                                                        @input="page[5].city = $event" />,
                                                    <SpellCheckedText :value="page[5].stateProvince || 'N/A'"
                                                        @input="page[5].stateProvince = $event" />
                                                </p>
                                            </div>
                                            <div v-if="footerImage" class="bottom-gradient">
                                                <img :src="footerImage" class="w-full h-full" />
                                            </div>
                                        </div>
                                        <div v-else class="empty-slot" />
                                    </div>
                                    <div class="project-card card-small">
                                        <div v-if="page[6]" class="card-inner">
                                            <PhotoBox class="photobox" :project="page[6]"
                                                :bgColor="projects.style.colorScheme.body" />
                                            <div class="overlay">
                                                <p class="p-name">
                                                    <SpellCheckedText :value="page[6].projectName || 'N/A'"
                                                        @input="page[6].projectName = $event" />
                                                </p>
                                                <p class="p-loc">
                                                    <SpellCheckedText :value="page[6].city || 'N/A'"
                                                        @input="page[6].city = $event" />,
                                                    <SpellCheckedText :value="page[6].stateProvince || 'N/A'"
                                                        @input="page[6].stateProvince = $event" />
                                                </p>
                                            </div>
                                            <div v-if="footerImage" class="bottom-gradient">
                                                <img :src="footerImage" class="w-full h-full" />
                                            </div>
                                        </div>
                                        <div v-else class="empty-slot" />
                                    </div>
                                    <div class="project-card card-medium">
                                        <div v-if="page[7]" class="card-inner">
                                            <PhotoBox class="photobox" :project="page[7]"
                                                :bgColor="projects.style.colorScheme.body" />
                                            <div class="overlay">
                                                <p class="p-name">
                                                    <SpellCheckedText :value="page[7].projectName || 'N/A'"
                                                        @input="page[7].projectName = $event" />
                                                </p>
                                                <p class="p-loc">
                                                    <SpellCheckedText :value="page[7].city || 'N/A'"
                                                        @input="page[7].city = $event" />,
                                                    <SpellCheckedText :value="page[7].stateProvince || 'N/A'"
                                                        @input="page[7].stateProvince = $event" />
                                                </p>
                                            </div>
                                            <div v-if="footerImage" class="bottom-gradient">
                                                <img :src="footerImage" class="w-full h-full" />
                                            </div>
                                        </div>
                                        <div v-else class="empty-slot" />
                                    </div>
                                    <div class="project-card card-large">
                                        <div v-if="page[8]" class="card-inner">
                                            <PhotoBox class="photobox" :project="page[8]"
                                                :bgColor="projects.style.colorScheme.body" />
                                            <div class="overlay">
                                                <p class="p-name">
                                                    <SpellCheckedText :value="page[8].projectName || 'N/A'"
                                                        @input="page[8].projectName = $event" />
                                                </p>
                                                <p class="p-loc">
                                                    <SpellCheckedText :value="page[8].city || 'N/A'"
                                                        @input="page[8].city = $event" />,
                                                    <SpellCheckedText :value="page[8].stateProvince || 'N/A'"
                                                        @input="page[8].stateProvince = $event" />
                                                </p>
                                            </div>
                                            <div v-if="footerImage" class="bottom-gradient">
                                                <img :src="footerImage" class="w-full h-full" />
                                            </div>
                                        </div>
                                        <div v-else class="empty-slot" />
                                    </div>
                                </div>

                                <!-- ROW 4 -->
                                <div class="content-row row-4">
                                    <div class="project-card card-xlarge">
                                        <div v-if="page[9]" class="card-inner">
                                            <PhotoBox class="photobox" :project="page[9]"
                                                :bgColor="projects.style.colorScheme.body" />
                                            <div class="overlay">
                                                <p class="p-name">
                                                    <SpellCheckedText :value="page[9].projectName || 'N/A'"
                                                        @input="page[9].projectName = $event" />
                                                </p>
                                                <p class="p-loc">
                                                    <SpellCheckedText :value="page[9].city || 'N/A'"
                                                        @input="page[9].city = $event" />,
                                                    <SpellCheckedText :value="page[9].stateProvince || 'N/A'"
                                                        @input="page[9].stateProvince = $event" />
                                                </p>
                                            </div>
                                            <div v-if="footerImage" class="bottom-gradient">
                                                <img :src="footerImage" class="w-full h-full" />
                                            </div>
                                        </div>
                                        <div v-else class="empty-slot" />
                                    </div>
                                    <div class="project-card card-medium">
                                        <div v-if="page[10]" class="card-inner">
                                            <PhotoBox class="photobox" :project="page[10]"
                                                :bgColor="projects.style.colorScheme.body" />
                                            <div class="overlay">
                                                <p class="p-name">
                                                    <SpellCheckedText :value="page[10].projectName || 'N/A'"
                                                        @input="page[10].projectName = $event" />
                                                </p>
                                                <p class="p-loc">
                                                    <SpellCheckedText :value="page[10].city || 'N/A'"
                                                        @input="page[10].city = $event" />,
                                                    <SpellCheckedText :value="page[10].stateProvince || 'N/A'"
                                                        @input="page[10].stateProvince = $event" />
                                                </p>
                                            </div>
                                            <div v-if="footerImage" class="bottom-gradient">
                                                <img :src="footerImage" class="w-full h-full" />
                                            </div>
                                        </div>
                                        <div v-else class="empty-slot" />
                                    </div>
                                    <div class="project-card card-normal">
                                        <div v-if="page[11]" class="card-inner">
                                            <PhotoBox class="photobox" :project="page[11]"
                                                :bgColor="projects.style.colorScheme.body" />
                                            <div class="overlay">
                                                <p class="p-name">
                                                    <SpellCheckedText :value="page[11].projectName || 'N/A'"
                                                        @input="page[11].projectName = $event" />
                                                </p>
                                                <p class="p-loc">
                                                    <SpellCheckedText :value="page[11].city || 'N/A'"
                                                        @input="page[11].city = $event" />,
                                                    <SpellCheckedText :value="page[11].stateProvince || 'N/A'"
                                                        @input="page[11].stateProvince = $event" />
                                                </p>
                                            </div>
                                            <div v-if="footerImage" class="bottom-gradient">
                                                <img :src="footerImage" class="w-full h-full" />
                                            </div>
                                        </div>
                                        <div v-else class="empty-slot" />
                                    </div>
                                </div>
                            </div>

                            <!-- TALL CARD RIGHT -->
                            <div class="project-card card-tall">
                                <div v-if="page[12]" class="card-inner">
                                    <PhotoBox class="photobox" :project="page[12]"
                                        :bgColor="projects.style.colorScheme.body" />
                                    <div class="overlay">
                                        <p class="p-name">
                                            <SpellCheckedText :value="page[12].projectName || 'N/A'"
                                                @input="page[12].projectName = $event" />
                                        </p>
                                        <p class="p-loc">
                                            <SpellCheckedText :value="page[12].city || 'N/A'"
                                                @input="page[12].city = $event" />,
                                            <SpellCheckedText :value="page[12].stateProvince || 'N/A'"
                                                @input="page[12].stateProvince = $event" />
                                        </p>
                                    </div>
                                    <div v-if="footerImage" class="bottom-gradient">
                                        <img :src="footerImage" class="w-full h-full" />
                                    </div>
                                </div>
                                <div v-else class="empty-slot" />
                            </div>
                        </div>

                    </template>

                    <template v-else>
                        <!-- Standard multi-row grid for overflow pages 16 items per page (4x4 grid) -->
                        <div class="overflow-grid">
                            <div v-for="n in 16" :key="n" class="project-card overflow-card">
                                <div v-if="page[n - 1]" class="card-inner">
                                    <PhotoBox class="photobox" :project="page[n - 1]"
                                        :bgColor="projects.style.colorScheme.body" />
                                    <div class="overlay">
                                        <p class="p-name">
                                            <SpellCheckedText :value="page[n - 1].projectName || 'N/A'"
                                                @input="page[n - 1].projectName = $event" />
                                        </p>
                                        <p class="p-loc">
                                            <SpellCheckedText :value="page[n - 1].city || 'N/A'"
                                                @input="page[n - 1].city = $event" />,
                                            <SpellCheckedText :value="page[n - 1].stateProvince || 'N/A'"
                                                @input="page[n - 1].stateProvince = $event" />
                                        </p>
                                    </div>
                                    <div v-if="footerImage" class="bottom-gradient">
                                        <img :src="footerImage" class="w-full h-full" />
                                    </div>
                                </div>
                                <div v-else class="empty-slot" />
                            </div>
                        </div>
                    </template>
                </div>

                <!-- FOOTER -->
                <div class="footer-section">
                    <div class="footer-left">
                        <SpellCheckedText :value="projects.opprName || 'BCIT Academic Center Project'"
                            @input="projects.opprName = $event" />
                    </div>
                    <div class="footer-right">
                        <SpellCheckedText :value="projects.submittedBy || ''" @input="projects.submittedBy = $event" />
                        <span class="divider">|</span>
                        <span class="page-num">{{ startPage + pageIndex
                            }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.main-container {
    display: flex;
    flex-direction: column;
    width: 297mm;
    min-height: 210mm;
    background: #f0f0f0;
    font-family: Helvetica, sans-serif;

    &:hover {
        .photobox :deep(.photo-overlay) {
            opacity: 1;
        }

        .photobox :deep(img) {
            opacity: 0.9;
        }
    }

    .sub-container {
        width: 297mm;
        height: 210mm;
        background: white;
        padding-top: 60px;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        position: relative;
        overflow: hidden;
    }

    .page-inner {
        display: flex;
        flex-direction: column;
        height: 100%;
        width: 100%;
    }

    .project-rows {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 15px;
        padding: 0px 40px;


        .bottom-wrapper {
            display: flex;
            gap: 15px;
            width: 100%;
            height: 275px;

            .left-col {
                display: flex;
                flex-direction: column;
                gap: 15px;
                flex: 3.4;
            }

            .card-tall {
                flex: 0.95;
            }
        }

        .content-row {
            display: flex;
            gap: 15px;
            width: 100%;

            &.row-1 {
                height: 245px;
                padding-right: 70px;

                .card-normal {
                    flex: 1;
                }
            }

            &.row-2 {
                height: 130px;

                .card-xlarge {
                    flex: 1.35;
                }

                .card-normal {
                    flex: 1;
                }

                .card-small {
                    flex: 0.85;
                }
            }

            &.row-3 {
                height: 130px;

                .card-small {
                    flex: 0.85;
                }

                .card-normal {
                    flex: 1;
                }

                .card-large {
                    flex: 1.25;
                }

                .card-medium {
                    flex: 1.15;
                }

            }

            &.row-4 {
                height: 130px;

                .card-xlarge {
                    flex: 1.7;
                }

                .card-normal {
                    flex: 1;
                }

                .card-medium {
                    flex: 1.4;
                }
            }
        }
    }

    .about-box {
        flex: 1.2;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        box-sizing: border-box;
        overflow: hidden;

        .main-header {
            font-size: var(--heading-size);
            color: var(--heading-color);
            font-weight: var(--heading-weight);
            font-style: var(--heading-style);
            text-decoration: var(--heading-decoration);
            font-family: var(--heading-font);
            background: var(--header-color);
        }

        .about-sec {
            min-height: 170px;
            margin-top: auto;
            padding: 20px;
            background: var(--body-color);
            display: flex;
            flex-direction: column;
            text-align: center;

            .about-title {
                font-size: var(--label-size);
                font-weight: var(--label-weight);
                color: var(--label-color);
                font-style: var(--label-style);
                text-decoration: var(--label-decoration);
                font-family: var(--label-font);
                margin-bottom: 8px;
            }

            .about-description {
                font-size: var(--text-size);
                font-weight: var(--text-weight);
                color: var(--text-color);
                font-style: var(--text-style);
                text-decoration: var(--text-decoration);
                font-family: var(--text-font);
                line-height: 1.3;
                white-space: pre-wrap;
            }
        }
    }

    .project-card {
        position: relative;
        background: var(--body-color);
        overflow: hidden;
        height: 100%;

        .bottom-gradient {
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 120px;
            z-index: 2;
            pointer-events: none;
        }

        .card-inner {
            width: 100%;
            height: 100%;
            position: relative;
        }

        .overlay {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            padding: 8px 10px;
            color: white;
            text-align: left;
            z-index: 50;

            .p-name {
                font-size: 10px;
                font-weight: bold;
                font-style: italic;
                font-family: Helvetica;
                color: white;
                margin: 0;
                line-height: 1.2;
                margin-bottom: 2px;
            }

            .p-loc {
                font-size: 10px;
                font-weight: bold;
                font-style: italic;
                font-family: Helvetica;
                color: white;
                margin: 0;
            }
        }
    }

    .empty-slot {
        width: 100%;
        height: 100%;
        background: var(--body-color);
        opacity: 0.5;
    }

    .overflow-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        grid-template-rows: repeat(4, 1fr);
        gap: 15px;
        height: 100%;
    }

    .footer-section {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 15px 70px;
        font-size: 12px;
        color: var(--footer-color);
        font-weight: bold;

        .footer-right {
            display: flex;
            align-items: center;

            .divider {
                margin: 0 10px;
            }

            .page-num {
                color: var(--heading-color);
                font-size: 16px;
            }
        }
    }
}
</style>
